/**
 * 교육실적 ag-grid 인라인 편집 지원
 * - applyProgramEditing: programColDefs 트리에 editable/cellEditor/valueSetter(검증) 주입
 * - mapProgramRowToEditDTO: 그리드 행 → PATCH /program/{id} 요청 바디 매핑
 *
 * 편집 활성화는 grid context.editMode 로 제어(토글 시 refreshCells).
 */
import { message } from 'antd'
import { PROGRAM_FIELD_SPEC, validateProgramField } from 'utils/validation/programEditSpec'

/**
 * 인라인 편집 제외 컬럼
 * - programId/regionId/areaName : 식별자·파생 표시값
 * - status                      : 운영상태는 인라인 편집 대상에서 제외(정책)
 * - registrationYear            : 연도 필터의 기준값(강사 그리드와 동일). 아래 상세 설명 참고
 * - isCompleted                 : 아카이브는 전용 API(PATCH /program/{id}/archive) 사용
 * - mainTutorName/subTutorName  : 강사 매칭은 상세 화면의 강사 목록에서 관리
 * - isShortTerm                 : 교육시간으로 파생되는 값
 * - createUser/updateUser/createdAt/updatedAt/detail/*Count : 표시 전용
 */
export const PROGRAM_EDIT_EXCLUDED = new Set([
    'programId',
    'regionId',
    'areaName',
    'status',
    'registrationYear',
    'isCompleted',
    'isShortTerm',
    'mainTutorName',
    'subTutorName',
    'mainTutorCount',
    'subTutorCount',
    'detail',
    'createUser',
    'updateUser',
    'createdAt',
    'updatedAt',
])

/**
 * 편집 가능 기본 조건.
 * 그룹 행(행 그룹화 시 생기는 집계 행)은 params.data 가 없어 valueSetter 가 터지므로 함께 막는다.
 */
const editableByMode = params => !!params.context?.editMode && !params.node?.group

/**
 * 도서벽지 기관은 기관명·주소를 인라인에서 잠근다.
 * 상세 화면에는 "기관명 입력 → 도서벽지 목록 조회 → 주소 자동입력(또는 주소 선택)" 흐름이 있는데,
 * 셀 단위 편집으로는 그 조회/연동을 재현할 수 없어 잘못된 기관명·주소가 저장될 수 있다.
 * → 도서벽지 행은 상세 화면에서 수정하도록 유도한다.
 */
const ISLAND = '도서벽지'
const ISLAND_LOCKED_FIELDS = new Set(['organizationName', 'address'])
const editableUnlessIsland = params => editableByMode(params) && params.data?.organizationType !== ISLAND

/**
 * 소외여부: 도서벽지/특수학교/지역아동센터는 정의상 항상 소외.
 * 상세 화면(DemandAgencyInfoForm)과 동일하게 구분을 바꾸면 자동으로 맞추고, 직접 수정은 막는다.
 */
const ALWAYS_UNDERSERVED = [ISLAND, '특수학교', '지역아동센터']
const editableUnlessForcedUnderserved = params =>
    editableByMode(params) && !ALWAYS_UNDERSERVED.includes(params.data?.organizationType)

/**
 * ag-grid 는 데이터 값을 보고 컬럼 타입을 추론한다(값이 숫자면 number 컬럼).
 * number 로 추론된 컬럼에 문자열을 넣으면 셀이 "Invalid Number" 로 표시되므로,
 * 원래 값이 숫자였고 문자열이 숫자로 정확히 왕복되면 숫자로 맞춰 저장한다.
 * 앞자리 0 처럼 왕복되지 않는 값은 문자열 그대로 둔다(값이 깎이면 안 되므로).
 */
const keepNumericType = (prev, next) =>
    typeof prev === 'number' && typeof next === 'string' && next !== '' && String(Number(next)) === next
        ? Number(next)
        : next

// 공통 valueSetter: 검증 통과 시에만 반영 (실패 시 셀 값 유지 + 안내)
const makeValueSetter = (key, transform) => params => {
    const raw = typeof params.newValue === 'string' ? params.newValue.trim() : params.newValue
    const next = transform ? transform(raw) : keepNumericType(params.data?.[key], raw)
    const msg = validateProgramField(key, next, { ...params.data, [key]: next })
    if (msg) {
        message.error(msg)
        return false
    }
    params.data[key] = next
    return true
}

/**
 * 구분(organizationType) 전용 valueSetter.
 * 값 변경이 같은 행의 다른 셀에 영향을 주므로 세 가지를 함께 처리한다.
 *  1) 도서벽지로의 '전환'은 막는다 — 아래 상세 설명 참고
 *  2) 소외 3종이면 소외여부를 자동으로 true, 그 외로 바꾸면 false (상세 화면과 동일한 규칙)
 *  3) 행 전체 refresh — 기관명·주소·소외여부의 잠금 표시(회색)는 cellClass 로 그려지는데
 *     셀 하나만 바뀌면 형제 셀은 다시 그려지지 않아 색이 그대로 남는다.
 */
const organizationTypeValueSetter = params => {
    const next = typeof params.newValue === 'string' ? params.newValue.trim() : params.newValue
    const prev = params.data?.organizationType

    /*
     * 도서벽지로 바꾸는 것만 막는다(도서벽지 → 다른 구분은 허용).
     * 도서벽지 기관은 기관명·주소가 도서벽지 목록(checkIsland)과 일치해야 하는데,
     * 구분만 도서벽지로 바뀌면 기관명·주소는 이전 값 그대로 남은 채 잠겨버려
     * 인라인에서 고칠 수 없는 잘못된 조합이 그대로 저장된다.
     * 드롭다운에서 값을 숨기지는 않는다 — 선택은 되지만 반영하지 않고 이유를 안내한다.
     * (ag-grid 기본 select 에디터는 옵션 단위 disabled 를 지원하지 않는다)
     */
    if (next === ISLAND && prev !== ISLAND) {
        message.error('도서벽지는 기관명·주소를 도서벽지 목록에서 조회해야 하므로 상세 화면에서 변경해주세요.')
        return false
    }

    const msg = validateProgramField('organizationType', next, { ...params.data, organizationType: next })
    if (msg) {
        message.error(msg)
        return false
    }
    params.data.organizationType = next
    params.data.isUnderserved = ALWAYS_UNDERSERVED.includes(next)

    // valueSetter 반환 전에 부르면 갱신 중인 상태와 겹치므로 다음 틱에 실행
    setTimeout(() => params.api?.refreshCells({ rowNodes: [params.node], force: true }), 0)
    return true
}

// 숫자 컬럼: 빈 값은 null, 그 외 Number 로 변환해서 저장
const toNumberOrNull = v => (v === '' || v === null || v === undefined ? null : Number(v))

/**
 * 컬럼 헤더 툴팁 — 편집 규칙과 주의사항을 마우스오버로 안내.
 * 유효성 메시지는 어겼을 때만 보이므로, 미리 알 수 있게 헤더에 붙인다.
 */
const HEADER_NOTES = {
    // 편집 불가 사유
    programId: '식별자이므로 수정할 수 없습니다.',
    regionId: '지역은 수정할 수 없습니다.',
    status: '운영상태는 상세 화면에서 변경합니다.',
    isCompleted: '아카이브는 상세 화면의 [아카이브] 버튼으로 처리합니다.',
    mainTutorName: '강사 매칭은 상세 화면에서 관리합니다.',
    subTutorName: '강사 매칭은 상세 화면에서 관리합니다.',
    isShortTerm: '교육시간에 따라 자동으로 정해집니다.',
    registrationYear: '활동연도는 연도 필터의 기준이므로 상세 화면에서 변경합니다.',

    // 편집 규칙
    educationType: '센터교육·찾아가는 교육·기타 중에서 선택합니다.',
    course: '정규과정·비정규과정 중에서 선택합니다.',
    grade: '초등·중등·고등·혼합 중에서 선택합니다.',
    classroom: '선택 항목입니다. 형식 제한 없이 입력합니다. (예: 6-1)',
    programName: '2자 이상 입력합니다. 숫자만으로는 입력할 수 없습니다.',
    category1: '필수 항목입니다. 목록에서 선택합니다(해당없음은 선택할 수 없습니다).',
    category2: '선택 항목입니다. 해당없음을 포함한 목록에서 선택합니다.',
    category3: '선택 항목입니다. 해당없음을 포함한 목록에서 선택합니다.',
    classHours: '0 이상의 숫자를 입력합니다.',
    numberOfSession: '선택 항목입니다. 입력할 경우 0 이상의 숫자여야 합니다.',
    numberOfApplicants: '0 이상의 숫자를 입력합니다.',
    // 미신청 참여자가 있어 수료인원이 수강인원보다 많을 수 있다 (programEditSpec 참고)
    numberOfGraduates: '0 이상의 숫자를 입력합니다.',
    startDate: 'YYYY-MM-DD 형식으로 실제 존재하는 날짜를 입력합니다.',
    endDate: 'YYYY-MM-DD 형식으로 입력합니다. 시작일보다 빠를 수 없습니다.',
    isUnderserved: '도서벽지·특수학교·지역아동센터는 자동으로 소외 처리되며 수정할 수 없습니다.',
    organizationType: '변경하면 소외여부가 자동으로 조정됩니다. 도서벽지로 변경하는 것은 상세 화면에서만 가능합니다.',
    organizationName: '2자 이상 입력합니다. 숫자만으로는 입력할 수 없습니다. 도서벽지는 상세 화면에서만 수정합니다.',
    address: '2자 이상 입력합니다. 숫자만으로는 입력할 수 없습니다. 도서벽지는 상세 화면에서만 수정합니다.',
    // 수요처 담당자는 유효성 검사를 두지 않는다 (programEditSpec 참고)
    managerName: '선택 항목입니다. 형식 제한 없이 입력합니다.',
    managerPosition: '선택 항목입니다. 형식 제한 없이 입력합니다.',
    managerTelNumber: '선택 항목입니다. 형식 제한 없이 입력합니다.',
    managerPhoneNumber: '선택 항목입니다. 형식 제한 없이 입력합니다.',
    managerEmail: '선택 항목입니다. 형식 제한 없이 입력합니다.',
    managerRemark: '선택 항목입니다. 형식 제한 없이 입력합니다.',
}

/** 헤더 툴팁 주입 (편집 여부와 무관하게 모든 컬럼에 적용) */
const withHeaderNote = col => {
    const note = HEADER_NOTES[col.field]
    return note ? { ...col, headerTooltip: note } : col
}

/**
 * 활동연도를 인라인 편집에서 뺀 이유 (강사 그리드도 같은 이유로 제외한다)
 *  1) 그리드는 진입 시 당해연도 필터가 걸려 있어(AgGrid onGridReady), 연도를 바꾸면
 *     그 행이 즉시 화면에서 사라진다. 미저장 상태라 편집 종료도 막히는데 취소할 행을
 *     찾을 수 없어 빠져나갈 방법이 없어진다.
 *  2) 값이 숫자(2026)라 ag-grid 가 number 타입으로 추론하는데, 드롭다운 옵션은 문자열이라
 *     목록이 전부 "Invalid Number" 로 표시된다. 되살리려면 옵션도 숫자로 넘겨야 한다.
 * → 활동연도는 상세 화면(OperationInfoForm)에서 수정한다.
 *
 * 아래는 활동연도와 같은 field 를 공유하는 필터·그룹 전용 컬럼.
 * 활동연도를 다시 편집 대상으로 되돌리더라도 이 컬럼은 편집 대상이 되면 안 된다.
 */
const EDIT_EXCLUDED_COL_IDS = new Set(['registrationYearFilter'])

/** 단일 컬럼에 편집 속성 주입 (해당되면 새 객체 반환, 아니면 원본) */
const decorateColumn = col => {
    if (!col || !col.field || PROGRAM_EDIT_EXCLUDED.has(col.field)) return col
    if (EDIT_EXCLUDED_COL_IDS.has(col.colId)) return col

    const { field } = col
    const spec = PROGRAM_FIELD_SPEC[field]
    if (!spec) return col // 스펙에 없는 컬럼은 읽기 전용 유지

    // 행 값에 따라 잠기는 필드는 params 를 보고 판단하는 editable 함수를 쓴다
    let editable = editableByMode
    if (ISLAND_LOCKED_FIELDS.has(field)) editable = editableUnlessIsland
    if (field === 'isUnderserved') editable = editableUnlessForcedUnderserved

    if (spec.editor === 'boolean') {
        // ag-grid 기본 boolean 에디터(체크박스)
        return { ...col, editable, cellDataType: 'boolean' }
    }

    if (spec.editor === 'select') {
        return {
            ...col,
            editable,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: { values: spec.options },
            valueSetter: field === 'organizationType' ? organizationTypeValueSetter : makeValueSetter(field),
        }
    }

    if (spec.editor === 'number') {
        return {
            ...col,
            editable,
            cellEditor: 'agNumberCellEditor',
            valueSetter: makeValueSetter(field, toNumberOrNull),
        }
    }

    // text 계열
    return {
        ...col,
        editable,
        cellEditor: 'agTextCellEditor',
        valueSetter: makeValueSetter(field),
    }
}

/** programColDefs(그룹 포함) 전체에 편집 속성 + 헤더 툴팁 주입 */
export const applyProgramEditing = colDefs =>
    colDefs.map(col => {
        if (col.children) {
            return { ...col, children: col.children.map(c => withHeaderNote(decorateColumn(c))) }
        }
        return withHeaderNote(decorateColumn(col))
    })

/**
 * 요청 바디에서 빼는 필드.
 * - 표시·파생 전용         : 서버가 다시 계산하거나 목록 표시에만 쓰는 값
 * - files                  : editProgram 이 files 배열의 원소를 multipart 파트로 붙이기 때문에,
 *                            첨부 메타데이터가 섞여 들어가면 잘못된 첨부가 올라간다.
 *                            필드를 빼두면 컨트롤러가 첨부를 건드리지 않아 기존 첨부가 보존된다.
 */
const DTO_OMITTED_FIELDS = [
    'areaName',
    'isShortTerm',
    'mainTutorName',
    'subTutorName',
    'mainTutorCount',
    'subTutorCount',
    'createUser',
    'updateUser',
    'createdAt',
    'updatedAt',
    'files',
]

const omitForDTO = obj => {
    const rest = { ...obj }
    DTO_OMITTED_FIELDS.forEach(key => delete rest[key])
    return rest
}

/**
 * 그리드 행 + 상세 응답 → PATCH /program/{id} 바디.
 *
 * 이 API 는 부분 수정이 아니라 전체 갱신이다. 그래서 목록 응답에 없는 필드는 빼고 보내면
 * 서버에서 그대로 지워진다. 실제로 강사 매칭(tutors)이 그랬다 — 목록에는 표시용 문자열
 * mainTutorName/subTutorName 만 있고 매칭 정보 자체가 없어서, 인라인 저장 시 주강사·보조강사가
 * 사라졌다. 목록 행만으로는 복원할 수 없는 값이다.
 *
 * → 저장 직전에 받아온 상세(full, GET /program/{id})를 바탕으로 깔고,
 *   그 위에 그리드에서 고친 값을 덮는다. 목록에 없는 필드는 상세 값이 그대로 살아남는다.
 *
 * full 없이 부르면 예전처럼 행 값만 보내게 되어 같은 사고가 나므로, 호출부(AgGrid)는
 * 상세 조회에 실패하면 저장을 중단한다.
 */
export const mapProgramRowToEditDTO = (row, full) => {
    const base = omitForDTO(full || {})
    const edited = omitForDTO(row)

    // 목록에 없는 필드가 undefined 로 상세 값을 덮지 않도록 실제 값이 있는 키만 올린다
    Object.entries(edited).forEach(([key, value]) => {
        if (value !== undefined) base[key] = value
    })

    return base
}
